// The order deck (spec §5): five family cards, each with its own shape and colour. Tap a family and its
// order cards fan out; tap an order card and the map asks for its target. No dropdowns, ever.

import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { CARDS, FAMILY_META } from '../../sim/deck';
import type { CardDef, Family } from '../../sim/types';
import { useStore } from '../../state/store';
import { OrderCard } from '../cards/OrderCard';
import { cardReason } from '../cards/legality';
import { Chip, ShapeTile } from '../cards/ui';
import { transmitEstimateSec } from './delay';
import { SATURATED } from './NetMeter';
import { FragoCard } from './FragoCard';

/** Family order on the rail, left to right; also the tab order of the fan-out. */
const FAMILIES: { id: Family; label: string }[] = [
  { id: 'maneuver', label: 'Maneuver' },
  { id: 'fires', label: 'Fires' },
  { id: 'isr', label: 'ISR' },
  { id: 'sustain', label: 'Sustain' },
  { id: 'command', label: 'Command' },
];

/**
 * How much of the order-card list is always on screen, in CSS pixels (border box).
 *
 * The deck is the hand: every card in the open family has to be reachable by scrolling this list, and
 * a list that has collapsed to a sliver is not scrollable in any useful sense. At 1440x900 with a unit
 * selected the rail gave it 68 px against 362 px of cards - sixteen when the review measured it - and
 * the deck was simply unusable. This floor is the fix: the advisors above are the rail's one flexible
 * member and give up the space instead. Less the 1 px top border, it measures 177 px of `clientHeight`,
 * which is two and a half order cards and a clear invitation to scroll.
 */
const LIST_PX = 178;

export function Deck() {
  const [open, setOpen] = useState<Family>('maneuver');
  const [fragoOpen, setFragoOpen] = useState(false);
  const deckRef = useRef<HTMLElement>(null);
  const snapshot = useStore((s) => s.snapshot);
  const selectedUnit = useStore((s) => s.selectedUnit);
  const targeting = useStore((s) => s.targeting);
  const grid = useStore((s) => s.grid);
  const scenario = useStore((s) => s.scenario);
  const dials = useStore((s) => s.dials);
  const issue = useStore((s) => s.issue);
  const startTargeting = useStore((s) => s.startTargeting);
  const stopTargeting = useStore((s) => s.stopTargeting);

  const unit = useMemo(
    () => (snapshot && selectedUnit !== null ? snapshot.units.find((u) => u.id === selectedUnit) ?? null : null),
    [snapshot, selectedUnit],
  );
  const hqAlive = useMemo(
    () => (snapshot ? snapshot.units.some((u) => u.side === 0 && u.type === 'hq' && u.alive) : true),
    [snapshot],
  );

  const queueSec = snapshot?.net.queueSec ?? 0;
  const saturated = (snapshot?.net.occupancy ?? 0) >= SATURATED;
  const waitSec = saturated || queueSec > 0 ? queueSec : 0;
  const txSec = transmitEstimateSec(dials, unit, hqAlive);

  const cards = CARDS.filter((c) => c.family === open);

  // A different unit, or losing the selection, abandons an in-progress FRAGO the same way it already
  // abandons in-progress map targeting (`select` in `src/state/store.ts`).
  useEffect(() => setFragoOpen(false), [selectedUnit]);

  // The same check the radial order menu greys its wedges with (src/ui/cards/legality.ts).
  function reasonFor(def: CardDef): string | undefined {
    if (!unit) return 'Select a unit on the map first';
    if (!grid || !scenario || !snapshot) return 'The simulation is still loading';
    return cardReason(def, unit, grid, scenario.graphics, snapshot.units);
  }

  function tap(def: CardDef) {
    if (!unit) return;
    // The FRAGO (fragmentary order) card has no map gesture of its own: it opens the free-text panel in
    // this same rail instead of entering the global targeting mode (spec §6 "free-text FRAGO ... a
    // convenience on top of the deck").
    if (def.targetKind === 'text') {
      setFragoOpen(true);
      return;
    }
    if (targeting?.card === def.id) {
      stopTargeting();
      return;
    }
    // Cards that need no target are played the moment they are tapped.
    if (def.targetKind === 'none') issue(unit.id, def.id, { kind: 'none' });
    else startTargeting(def.id);
  }

  // `shrink-0`, not `flex-1`: the deck and the advisor rail were both flexible members of the same
  // column and the deck lost every time. It now takes its natural height - header, the five family
  // tiles, and an order-card list with a hard floor of LIST_MIN_PX that scrolls its own overflow - and
  // the advisors above are the single thing that gives up space.
  return (
    <section ref={deckRef} data-testid="deck" className="wt-panel flex w-full min-w-0 shrink-0 flex-col">
      <header className="wt-panel-head">
        <span>Order deck</span>
        {unit ? (
          <Chip tone="friendly" active>{unit.callsign}</Chip>
        ) : (
          <Chip>select a unit</Chip>
        )}
      </header>

      <div className="flex items-start justify-between gap-1 px-2 py-2">
        {FAMILIES.map((f) => {
          const meta = FAMILY_META[f.id];
          const active = open === f.id;
          return (
            <button
              key={f.id}
              type="button"
              data-testid={`family-${f.id}`}
              data-active={active ? 'true' : 'false'}
              onClick={() => { setOpen(f.id); setFragoOpen(false); }}
              className="flex w-[58px] flex-col items-center gap-1"
              title={`${f.label} orders`}
            >
              <ShapeTile meta={meta} size={44} active={active}>
                <span
                  className="mono text-[13px] font-semibold"
                  style={{ color: active ? 'var(--color-paper)' : meta.color }}
                >
                  {f.label[0]}
                </span>
              </ShapeTile>
              <span
                className="mono text-[9px] uppercase tracking-[0.1em]"
                style={{ color: active ? meta.color : 'var(--color-fog)' }}
              >
                {f.label}
              </span>
            </button>
          );
        })}
      </div>

      <div
        data-testid="deck-list"
        className="thin-scroll space-y-1 overflow-y-auto border-t border-slate-700 px-2 py-2"
        style={{ height: LIST_PX }}
      >
        {cards.map((def) => (
          <OrderCard
            key={def.id}
            def={def}
            meta={FAMILY_META[def.family]}
            issueSec={def.issueSec}
            txSec={txSec}
            execSec={def.execSec}
            reason={reasonFor(def)}
            waitSec={waitSec}
            active={targeting?.card === def.id}
            onTap={() => tap(def)}
          />
        ))}
      </div>

      {/* The FRAGO composer is a floating panel anchored above the deck, not a card inside the list:
          it is a textarea, a parse, and a list of parsed orders to confirm, and none of that fits in
          the strip of rail the deck's own list occupies. */}
      {fragoOpen && unit && grid && scenario && snapshot && (
        <FragoPanel anchor={deckRef}>
          <FragoCard issuer={unit} onClose={() => setFragoOpen(false)} />
        </FragoPanel>
      )}
    </section>
  );
}

/** Room above the panel for the top bar, so the composer is never tucked under it. */
const PANEL_TOP_GUTTER = 60;

/**
 * Floats its children directly above `anchor`, matching its width, inside the viewport.
 *
 * Fixed rather than absolute so it escapes the rail's `overflow-hidden`, and measured rather than
 * guessed so it follows the deck when the rail reflows or the window resizes.
 *
 * Portalled to `document.body` because the right rail carries `backdrop-filter`, which makes it the
 * containing block for its fixed-position descendants: rendered in place, the panel's viewport
 * coordinates were resolved against the rail instead and it landed 700 px off the right of the screen.
 */
function FragoPanel({ anchor, children }: { anchor: React.RefObject<HTMLElement | null>; children: React.ReactNode }) {
  const [box, setBox] = useState<CSSProperties | null>(null);

  useLayoutEffect(() => {
    const measure = (): void => {
      const el = anchor.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setBox({
        position: 'fixed',
        left: r.left,
        width: r.width,
        bottom: window.innerHeight - r.top + 8,
        maxHeight: Math.max(160, r.top - PANEL_TOP_GUTTER),
        zIndex: 30,
        overflowY: 'auto',
      });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [anchor]);

  if (!box || typeof document === 'undefined') return null;
  return createPortal(
    <div data-testid="frago-panel" className="thin-scroll fade-in" style={box}>
      {children}
    </div>,
    document.body,
  );
}
